/**
 * reconstructCajaFromAuditoria.ts — Recuperación defensiva ACID de ventas.
 *
 * QUÉ HACE:
 *   Si el historial de ventas (`caja_ventas_historial`) pierde registros por una
 *   corrupción/pérdida (típico en APK ante cierres abruptos), pero Auditoría sí
 *   conserva el evento de la venta, reconstruye una venta PARCIAL a partir del
 *   log de Auditoría para que los totales, resúmenes y cierres nunca queden
 *   incompletos. "Si Auditoría puede recuperarlo, Caja y Ventas también."
 *
 * QUÉ INVOCA / DEPENDE:
 *   - transactionalStore (lectura/escritura ACID de `auditoria_logs_v1` y
 *     `caja_ventas_historial`). Nunca localStorage directo.
 *
 * QUÉ EXPORTA: reconstructMissingVentasFromAuditoria(): number
 *
 * QUÉ NO DEBE HACER:
 *   - No inventa líneas de producto (Auditoría no las guarda): la venta
 *     reconstruida lleva `productos: []` y `reconstruidaDesdeAuditoria: true`.
 *   - No resucita ventas anuladas como activas (respeta el estado del evento).
 *   - No importa React, hooks ni UI.
 */

import {
  runTransaction,
  txRead,
  txWrite,
} from "@/lib/persistence/transactionalStore";
import type { Log } from "@/lib/audit/types";
import type { MovimientoCaja, Venta } from "../types/caja.types";

const AUDIT_LOGS_KEY = "auditoria_logs_v1";
const VENTAS_KEY = "caja_ventas_historial";
const MOVIMIENTOS_KEY = "caja_movimientos";

/** Acciones de Auditoría que representan una venta creada. */
const SALE_ACTIONS = new Set(["caja_venta_completada", "venta_creada"]);
/** Acciones que marcan una venta como anulada/eliminada. */
const CANCEL_ACTIONS = new Set([
  "facturacion_venta_anulada",
  "venta_eliminada",
]);

/** Normaliza una etiqueta de método de pago a su id inmutable en minúsculas. */
function normalizeMetodoFromLabel(label: unknown): string {
  return String(label ?? "")
    .trim()
    .toLowerCase();
}

/** Construye una venta parcial a partir de un log de venta de Auditoría. */
function ventaFromSaleLog(log: Log, anulada: boolean): Venta {
  const details = log.details ?? {};
  const metodoLabel = details.metodoPago;
  return {
    id: String(log.entidadId),
    fecha: log.createdAt,
    total: Number(details.total) || 0,
    metodoPago: normalizeMetodoFromLabel(metodoLabel) || "efectivo",
    metodoPagoLabel:
      typeof metodoLabel === "string" && metodoLabel.trim()
        ? metodoLabel
        : undefined,
    productos: [],
    usuarioId: log.userId || "unknown",
    userName: log.userName || "Desconocido",
    ...(typeof details.sesionId === "string"
      ? { sesionId: details.sesionId }
      : {}),
    ...(typeof details.puntoCajaId === "string"
      ? { puntoCajaId: details.puntoCajaId }
      : {}),
    estado: anulada ? "anulada" : "activa",
    reconstruidaDesdeAuditoria: true,
  };
}

/**
 * Detecta ventas presentes en Auditoría pero ausentes del historial y las
 * re-inserta de forma parcial. Idempotente: solo agrega las que faltan.
 * @returns número de ventas reconstruidas.
 */
export function reconstructMissingVentasFromAuditoria(): number {
  const logs = txRead<Log[] | null>(AUDIT_LOGS_KEY, null);
  if (!Array.isArray(logs) || logs.length === 0) return 0;

  const ventas = txRead<Venta[] | null>(VENTAS_KEY, null);
  const ventasArr: Venta[] = Array.isArray(ventas) ? ventas : [];
  const existingIds = new Set(ventasArr.map((v) => v.id));

  // IDs de ventas marcadas como anuladas/eliminadas en Auditoría.
  const cancelledIds = new Set<string>();
  for (const log of logs) {
    if (!CANCEL_ACTIONS.has(log.action)) continue;
    const id =
      (typeof log.details?.ventaId === "string" && log.details.ventaId) ||
      log.entidadId;
    if (id) cancelledIds.add(String(id));
  }

  // Recolectar ventas faltantes (un log por venta; si hay duplicados, el primero).
  const rebuilt = new Map<string, Venta>();
  for (const log of logs) {
    if (!SALE_ACTIONS.has(log.action)) continue;
    const id = log.entidadId ? String(log.entidadId) : "";
    if (!id || existingIds.has(id) || rebuilt.has(id)) continue;
    rebuilt.set(id, ventaFromSaleLog(log, cancelledIds.has(id)));
  }

  if (rebuilt.size === 0) return 0;

  const merged = [...ventasArr, ...rebuilt.values()].sort(
    (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime(),
  );

  runTransaction((): void => {
    txWrite(VENTAS_KEY, merged);
  });

  try {
    window.dispatchEvent(new CustomEvent("caja-ventas-reconstruidas"));
  } catch {
    /* entorno sin window: ignorar */
  }

  return rebuilt.size;
}

/* ─── Movimientos de caja ─────────────────────────────────────────────────── */

/** Acciones de Auditoría que representan un movimiento de caja creado. */
const MOV_REGISTERED_ACTIONS = new Set([
  "caja_retiro_registrado",
  "caja_gasto_registrado",
  "caja_ingreso_registrado",
  "caja_abrir_caja_registrado",
]);
/** Acción que cierra un gasto con vuelto (ajusta el monto al costo real). */
const GASTO_CIERRE_ACTION = "caja_gasto_cierre_finalizado";

type LogDetails = Record<string, unknown>;

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number {
  return Math.round(Number(v) || 0);
}

/** Construye un movimiento parcial a partir de un log de "registrado". */
function movimientoFromRegisteredLog(log: Log): MovimientoCaja | null {
  const id = String(log.entidadId ?? "");
  if (!id) return null;
  const d = (log.details ?? {}) as LogDetails;
  const tipo = str(d.tipo);
  if (!tipo) return null;

  const monto = num(d.monto);
  const vueltoPendiente = d.vueltoPendiente === true;
  const motivoBase = str(d.motivo);
  const descripcionDetallada = str(d.descripcionDetallada);
  const metodoPago = str(d.metodoPago);
  const puntoCajaId = str(d.puntoCajaId);
  const sesionId = str(d.sesionId);
  const authorizedBy = str(d.authorizedBy);

  return {
    id,
    tipo: tipo as MovimientoCaja["tipo"],
    monto,
    motivo: descripcionDetallada
      ? `${motivoBase} — ${descripcionDetallada}`
      : motivoBase,
    fecha: log.createdAt,
    usuarioId: log.userId || "unknown",
    userName: log.userName || "Desconocido",
    sesionId,
    ...(puntoCajaId ? { puntoCajaId } : {}),
    ...(metodoPago
      ? { metodoPago: metodoPago as MovimientoCaja["metodoPago"] }
      : {}),
    ...(authorizedBy ? { authorizedBy } : {}),
    ...(vueltoPendiente
      ? { gastoVueltoPendiente: true, montoSalida: monto }
      : {}),
    estado: "activo",
    reconstruidoDesdeAuditoria: true,
  };
}

interface CierreVueltoInfo {
  montoReal: number;
  vuelto: number;
  faltante: number;
  fecha: string;
}

/**
 * Detecta movimientos de caja presentes en Auditoría pero ausentes del
 * historial (`caja_movimientos`) y los re-inserta de forma parcial; además,
 * completa el vuelto de gastos cerrados (`caja_gasto_cierre_finalizado`).
 * Idempotente: solo agrega los que faltan y aplica cambios reales.
 *
 * @returns número de movimientos reconstruidos.
 */
export function reconstructMissingMovimientosFromAuditoria(): number {
  const logs = txRead<Log[] | null>(AUDIT_LOGS_KEY, null);
  if (!Array.isArray(logs) || logs.length === 0) return 0;

  const stored = txRead<MovimientoCaja[] | null>(MOVIMIENTOS_KEY, null);
  const movimientos: MovimientoCaja[] = Array.isArray(stored) ? stored : [];
  const existingIds = new Set(movimientos.map((m) => m.id));

  // 1) Cierres de gasto con vuelto (para completar el monto real).
  const cierres = new Map<string, CierreVueltoInfo>();
  for (const log of logs) {
    if (log.action !== GASTO_CIERRE_ACTION) continue;
    const d = (log.details ?? {}) as LogDetails;
    const movimientoId = str(d.movimientoId);
    if (!movimientoId) continue;
    cierres.set(movimientoId, {
      montoReal: num(d.montoReal),
      vuelto: num(d.vueltoMonto),
      faltante: num(d.faltanteMonto),
      fecha: log.createdAt,
    });
  }

  // 2) Recolectar movimientos faltantes (un log por movimiento).
  const rebuilt = new Map<string, MovimientoCaja>();
  for (const log of logs) {
    if (!MOV_REGISTERED_ACTIONS.has(log.action)) continue;
    const id = String(log.entidadId ?? "");
    if (!id || existingIds.has(id) || rebuilt.has(id)) continue;
    const mov = movimientoFromRegisteredLog(log);
    if (mov) rebuilt.set(id, mov);
  }

  const applyCierre = (m: MovimientoCaja): MovimientoCaja => {
    const c = cierres.get(m.id);
    if (!c) return m;
    const salida = Math.round(m.montoSalida ?? m.monto ?? 0);
    return {
      ...m,
      monto: c.montoReal,
      gastoVueltoPendiente: false,
      montoSalida: salida,
      vueltoDevuelto: c.vuelto,
      ...(c.faltante > 0 ? { faltanteAdicional: c.faltante } : {}),
      gastoVueltoCompletadoEn: c.fecha,
    };
  };

  const rebuiltArr = [...rebuilt.values()].map(applyCierre);

  let existingChanged = false;
  const nextExisting = movimientos.map((m) => {
    const c = cierres.get(m.id);
    if (!c) return m;
    if (!m.gastoVueltoPendiente && m.monto === c.montoReal) return m;
    existingChanged = true;
    return applyCierre(m);
  });

  if (rebuiltArr.length === 0 && !existingChanged) return 0;

  const merged = [...rebuiltArr, ...nextExisting].sort(
    (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime(),
  );

  runTransaction((): void => {
    txWrite(MOVIMIENTOS_KEY, merged);
  });

  try {
    window.dispatchEvent(new CustomEvent("caja-movimientos-updated"));
  } catch {
    /* entorno sin window: ignorar */
  }

  return rebuiltArr.length;
}
